import { FC, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Drawer, Menu, Button, Typography } from 'antd';
import UserAvatarPopover from 'components/UserAvatarPopover';
import { ExportOutlined } from '@ant-design/icons';
import { ActivityStatus, AugmentedUser } from 'types';
import { RootState } from 'store';
import { useAvatarSheet } from './useAvatarSheet';
import { groupUsersByStatus } from './avatarSheet.utils';
import styles from './AvatarSheet.module.less';
import appRoutes from '../../../../config/appRoutes';

const { Text } = Typography;

interface AvatarSheetProps {
  open: boolean;
  onClose: () => void;
  menuContent: React.ReactNode;
}

const STATUS_COLORS: Record<ActivityStatus, string> = {
  online: '#48E330',
  away: '#FAAD14',
  offline: '#9A9A9A',
};

const UserActivityRow: FC<{ user: AugmentedUser; loading?: boolean }> = ({ user, loading = false }) => {
  const displayText = user.activeLog?.task?.title ?? 'No active time log';

  return (
    <div className={styles.userRow}>
      <div className={styles.avatarWrap}>
        <UserAvatarPopover user={user} size="small" />
        <span className={styles.statusDot} style={{ backgroundColor: STATUS_COLORS[user.resolvedStatus] }} />
      </div>

      <div className={styles.userInfo}>
        <span className={styles.userName}>{user.name}</span>
         {loading ? (
          <span className={`${styles.skeletonBar} ${styles.skeletonActivityBar}`} style={{ width: 120 }} />
         ) : (
          <Text ellipsis className={styles.taskName}>{displayText}</Text>
         )}
      </div>
    </div>
  );
};

const SkeletonUserRow: FC<{ nameWidth?: number; taskWidth?: number }> = ({ nameWidth = 88, taskWidth = 130 }) => (
  <div className={styles.userRow}>
    <div className={styles.avatarWrap}>
      <span className={styles.skeletonCircle} />
    </div>
    <div className={styles.userInfo}>
      <span className={styles.skeletonBar} style={{ width: nameWidth, height: 13 }} />
      <span className={styles.skeletonBar} style={{ width: taskWidth, height: 11 }} />
    </div>
  </div>
);

const SkeletonList: FC = () => (
  <div className={styles.userList}>
    <div className={styles.statusSection}>
      <span className={styles.skeletonBar} style={{ width: 56, height: 11 }} />
    </div>
    {/* eslint-disable-next-line react/no-array-index-key */}
    {([{ n: 80, t: 130 }, { n: 95, t: 110 }, { n: 72, t: 145 }]).map((w, i) => (
      // eslint-disable-next-line react/no-array-index-key
      <SkeletonUserRow key={i} nameWidth={w.n} taskWidth={w.t} />
    ))}
    <div className={styles.statusSection}>
      <span className={styles.skeletonBar} style={{ width: 44, height: 11 }} />
    </div>
    {/* eslint-disable-next-line react/no-array-index-key */}
    {([{ n: 100, t: 120 }, { n: 84, t: 135 }]).map((w, i) => (
      // eslint-disable-next-line react/no-array-index-key
      <SkeletonUserRow key={i} nameWidth={w.n} taskWidth={w.t} />
    ))}
  </div>
);

const CurrentUserCard: FC<{ userData: any; currentUserLoading?: boolean }> = ({ userData, currentUserLoading = false }) => {
  const augmentedUser: AugmentedUser = {
    uuid: userData.uuid,
    name: userData.name,
    email: userData.email,
    avatar: userData.avatar || null,
    last_activity: userData.last_activity || '',
    activity_status: userData.activity_status || 'offline',
    resolvedStatus: userData.activity_status || 'offline',
    wonTask: null,
    activeLog: userData.activeLog || null,
  };

  return (
    <div className={styles.currentUserCard}>
      <UserActivityRow user={augmentedUser} loading={currentUserLoading} />
    </div>
  );
};

const TeamActivityPanel: FC<{ open: boolean; onNavigate?: () => void }> = ({ open, onNavigate }) => {
  const {
    users, loading, hasEverFetched, currentUserData, currentUserLoading, error, refetch,
  } = useAvatarSheet(open);
  const authUser = useSelector((store: RootState) => store.auth.user);

  const groupedUsers = useMemo(() => groupUsersByStatus(users), [users]);

  const displayUserData = currentUserData ?? authUser;
  const showSkeletonList = loading && !hasEverFetched;

  if (error && users.length === 0) {
    return (
      <div className={styles.panelState}>
        <span>Failed to load team activity.</span>
        <Button type="link" onClick={refetch}>
          Try again
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.teamActivityPanel}>
      {displayUserData && (
        <CurrentUserCard userData={displayUserData} currentUserLoading={currentUserLoading} />
      )}
      <div className={styles.panelHeader}>
        Team Activity
        <Link to={appRoutes.TEAM_ACTIVITY} className={styles.panelHeaderLink} onClick={onNavigate}>
          <ExportOutlined />
        </Link>
      </div>

       {showSkeletonList && <SkeletonList />}

       {!showSkeletonList && users.length > 0 && (
        <div className={styles.userList}>
          {groupedUsers.online.length > 0 && (
            <>
              <div className={styles.statusSection}>Online ({groupedUsers.online.length})</div>
              {groupedUsers.online.map((user) => (
                <UserActivityRow key={user.uuid} user={user} />
              ))}
            </>
          )}
          {groupedUsers.away.length > 0 && (
            <>
              <div className={styles.statusSection}>Away ({groupedUsers.away.length})</div>
              {groupedUsers.away.map((user) => (
                <UserActivityRow key={user.uuid} user={user} />
              ))}
            </>
          )}
          {groupedUsers.offline.length > 0 && (
            <>
              <div className={styles.statusSection}>Offline ({groupedUsers.offline.length})</div>
              {groupedUsers.offline.map((user) => (
                <UserActivityRow key={user.uuid} user={user} />
              ))}
            </>
          )}
        </div>
       )}

       {!showSkeletonList && users.length === 0 && !displayUserData && (
        <div className={styles.panelState}>
          <span>No team members found.</span>
        </div>
       )}
    </div>
  );
};

const AvatarSheet: FC<AvatarSheetProps> = ({ open, onClose, menuContent }) => {
  return (
    <Drawer
      visible={open}
      width={500}
      placement="right"
      onClose={onClose}
      bodyStyle={{ padding: 0 }}
      footer={null}
      className={styles.avatarSheet}
      destroyOnClose
    >
      <div className={styles.sheetBody}>
        <div className={styles.leftCol}>
          <Menu mode="inline" theme="light" selectable={false}>
            {menuContent}
          </Menu>
        </div>

        <div className={styles.colDivider} />

        <div className={styles.rightCol}>
          <TeamActivityPanel open={open} onNavigate={onClose} />
        </div>
      </div>
    </Drawer>
  );
};

export default AvatarSheet;
